import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import z from 'zod'
import type { ControlItem, ControlItemDefinition, ControlItemState } from '~/types/controls.types'
import type { Document } from '~/types/document.types'
import { arrayMergeByKey } from '~/utils/arrayMergeByKey'
import { flatToPrimeReactTree } from '~/utils/flatToPrimeReactTree'
import httpClient, { type ErrorObj } from '~/utils/httpClient'
import useToast from './toast'

const defCacheKey = 'controlDefinitionCache'

export const useControlDefinitionsList = () => {
  const q = useQuery({
    queryKey: ['controlDefinition'],
    queryFn: async () => {
      const cached = localStorage.getItem(defCacheKey)
      if (cached) {
        const cachedParsed = JSON.parse(cached) as ControlItemDefinition[]
        if (Array.isArray(cachedParsed)) return cachedParsed
      }
      const response = await httpClient.get<{ data: ControlItemDefinition[] }>(
        '/api/controls/definition',
      )
      if (!response?.data.data || !Array.isArray(response?.data.data)) {
        const error: ErrorObj = {
          statusCode: 0,
          error: true,
          code: 'unknown_error',
          message: `returned response isn't in expected array format`,
        }
        throw error
      }
      localStorage.setItem(defCacheKey, JSON.stringify(response?.data.data))
      return response?.data.data
    },
  })
  return q
}
export const useControlStateList = () => {
  const q = useQuery({
    queryKey: ['controlState'],
    queryFn: async () => {
      const response = await httpClient.get<{
        data: { controls: ControlItemState[]; documents: Document[] }
      }>('/api/controls/state')
      if (!response?.data.data || !Array.isArray(response?.data.data.controls)) {
        const error: ErrorObj = {
          statusCode: 0,
          error: true,
          code: 'unknown_error',
          message: `returned response isn't in expected array format`,
        }
        throw error
      }
      return response?.data.data
    },
  })
  return q
}
export const useControlList = () => {
  const {
    data: def,
    isLoading: isDefinitionLoading,
    refetch: definitionRefetch,
  } = useControlDefinitionsList() // Includes your custom fullRefresh

  const { data: state, isLoading: isStateLoading, refetch: stateRefetch } = useControlStateList()
  const isLoading = isDefinitionLoading || isStateLoading
  const data = useMemo(() => {
    if (
      def &&
      state &&
      Array.isArray(def) &&
      Array.isArray(state.controls) &&
      Array.isArray(state.documents)
    ) {
      const docsMap = new Map<number, Document>()
      for (const d of state.documents) {
        docsMap.set(d.id, d)
      }
      const arrayData = arrayMergeByKey('id', def as ControlItem[], state.controls as ControlItem[])
      for (const c of arrayData) {
        if (!c.documents) c.documents = []
        if (!c.controlDocumentMap?.length) continue
        for (const cd of c.controlDocumentMap) {
          if (c.documents?.findIndex((e) => e.id === cd.documentId) < 0) {
            const ld = docsMap.get(cd.documentId)
            if (ld) c.documents.push(ld)
          }
        }
      }
      const treeData = flatToPrimeReactTree(arrayData)
      return { arrayData, treeData, documents: state.documents }
    }
    return undefined
  }, [def, state])
  const fullRefetch = useCallback(() => {
    localStorage.removeItem(defCacheKey)
    return Promise.all([definitionRefetch(), stateRefetch()])
  }, [definitionRefetch, stateRefetch])
  return {
    arrayData: data?.arrayData,
    treeData: data?.treeData,
    documents: data?.documents,
    isLoading,
    isStateLoading,
    isDefinitionLoading,
    definitionRefetch,
    stateRefetch,
    fullRefetch,
  }
}

export const createNewDocumentSchema = z.object({
  controlId: z.number(),
  departmentId: z.number(),
  picUserId: z.number(),
  title: z.string().min(4),
  titleId: z.string().min(4).optional(),
  docNumberTemplate: z.string().optional(),
  docType: z.number().min(1).max(5),
})

export const useControlDocumentCreateMutation = () => {
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: async (p: z.infer<typeof createNewDocumentSchema>) => {
      const { error, data } = createNewDocumentSchema.safeParse(p)
      if (error) {
        console.error('document parameter creation mismatched', { payload: p, error })
        toast?.current?.show({
          summary: 'Some parameters are not correct in creating document',
          severity: 'error',
        })
        return
      }
      const { controlId, ...payload } = data
      return httpClient.put(`/api/controls/item/${controlId}/documents`, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries(['controlState'])
    },
    onError: (error, variables) => {
      console.error('error creating document', { payload: variables, error })
      toast?.current?.show({ summary: 'Failed to create document', severity: 'error' })
    },
  })
}
export const useControlDocumentLinkMutation = () => {
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ controlId, documentId }: { controlId: number; documentId: number }) =>
      httpClient.put(`/api/controls/item/${controlId}/documents/${documentId}`),
    onSuccess: (_, variables) => {
      qc.invalidateQueries(['controlState'])
      qc.invalidateQueries(['documents'])
      qc.invalidateQueries(['document', variables.documentId])
    },
    onError: (error, variables) => {
      console.error('error creating document', { variables, error })
      toast?.current?.show({ summary: 'Failed to create document' })
    },
  })
}
export const useControlDocumentRemoveLinkMutation = () => {
  const qc = useQueryClient()
  const toast = useToast()
  return useMutation({
    mutationFn: ({ controlId, documentId }: { controlId: number; documentId: number }) =>
      httpClient.delete(`/api/controls/item/${controlId}/documents/${documentId}`),
    onSuccess: (_, variables) => {
      qc.invalidateQueries(['controlState'])
      qc.invalidateQueries(['documents'])
      qc.invalidateQueries(['document', variables.documentId])
    },
    onError: (error, variables) => {
      console.error('error removing document from control', { variables, error })
      toast?.current?.show({ summary: 'Failed to remove document from control' })
    },
  })
}
